import { memo, useCallback, useEffect, useRef } from 'react';
import { View, StyleSheet, Text, Image, Pressable, Animated, Keyboard } from 'react-native';

import { I18n, Select, PlusButton, Navigation } from '../components';
import { COLOR, FONT, FONT_SIZE, THEME, __WEB__, SELECT_STYLE_ROUND } from '../../constants/styles';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import {
  getTopicsFromCurrentCase,
  getCurrentTopic,
  getProviderTopics,
  getOnCallTopics,
} from './selector';

import { changeCurrentTopic } from './actions';

import ConversationMembers from './conversation_members';
import OnCallMembers from './oncall_members';
import { topicLabel, topicLabelAirforce } from './ui_helpers';
import navModes from './nav_modes';
import FlavorMessage from './chat/FlavorMessage';
import SelectPatient from '../cases/select_case_button';

const ANIM_TIME = 250;

const shrinkAnim = new Animated.Value(200);
const fadeAnim = new Animated.Value(1);

let animating = false;
let collapsed = false;
let previousHeaderHeight = null;
let skipOneScrollTrigger = true;
let _screenVisible = false;
let _keyboardWillShowListener;

function mapStateToProps(state, ownProps) {
  const navMode = state.screens.messaging.navMode;
  const currentTopicId = state.application.currentTopicId;
  const currentUserId = state.user.userId;
  let convos = [];
  if (navMode === navModes.CASE) {
    convos = getTopicsFromCurrentCase(state);
  } else if (navMode === navModes.EMPLOYER) {
    convos = getProviderTopics(state);
  } else if (navMode === navModes.ONCALL) {
    convos = getOnCallTopics(state);
  }

  const selectedConvo = getCurrentTopic(state);

  const convoOptions = convos
    .map((topic) => {
      return {
        label: topicLabel(topic, currentUserId),
        id: topic.topicId,
        topic,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedOption = selectedConvo
    ? {
        id: selectedConvo.topicId,
        label: topicLabel(selectedConvo, currentUserId),
      }
    : null;

  const createConvoScreenTitle = 'Create Group Message';

  return {
    navMode,
    convos,
    selectedConvo,
    convoOptions,
    selectedOption,
    currentTopicId,
    createConvoScreenTitle,
  };
}
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ changeCurrentTopic }, dispatch),
  };
}

export default memo(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )((props) => {
    function handleSelectConvo({ topic }) {
      if (topic) props.actions.changeCurrentTopic(topic.topicId);
    }

    function handlePlusPress() {
      Navigation.showModal({ screen: 'CreateConversation', title: props.createConvoScreenTitle });
    }

    const headerHeight = props.navMode === navModes.CASE ? 200 : 150;
    if (!collapsed && !animating && headerHeight !== previousHeaderHeight) {
      shrinkAnim.setValue(headerHeight);
    }
    previousHeaderHeight = headerHeight;

    function collapseHeaders() {
      if (!_screenVisible || animating || collapsed) return;
      animating = true;
      Animated.parallel([
        Animated.timing(shrinkAnim, {
          toValue: 40,
          duration: ANIM_TIME,
          useNativeDriver: false,
        }),
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: ANIM_TIME,
          useNativeDriver: true,
        }),
      ]).start(function () {
        animating = false;
        collapsed = true;
      });
    }
    function expandHeaders() {
      if (!_screenVisible || animating || !collapsed) return;
      animating = true;
      Keyboard.dismiss();
      Animated.parallel([
        Animated.timing(shrinkAnim, {
          toValue: headerHeight,
          duration: ANIM_TIME,
          useNativeDriver: false,
        }),
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: ANIM_TIME,
          useNativeDriver: true,
        }),
      ]).start(function () {
        animating = false;
        collapsed = false;
      });
    }
    function handleArrowPress() {
      if (!collapsed) collapseHeaders();
      else expandHeaders();
    }
    function handleKeyboardShow() {
      collapseHeaders();
    }

    if (!__WEB__) {
      useEffect(() => {
        if (_screenVisible && !collapsed) collapseHeaders();
      }, [props.triggerScroll]);
      useEffect(() => {
        _screenVisible = props.screenVisible;
        if (_keyboardWillShowListener) _keyboardWillShowListener.remove();
        if (_screenVisible) {
          _keyboardWillShowListener = Keyboard.addListener('keyboardWillShow', handleKeyboardShow);
        }
      }, [props.screenVisible]);
      useEffect(() => {
        if (_screenVisible) {
          if (_keyboardWillShowListener) _keyboardWillShowListener.remove();
          _keyboardWillShowListener = Keyboard.addListener('keyboardWillShow', handleKeyboardShow);
        }
        return function () {
          _keyboardWillShowListener.remove();
          _keyboardWillShowListener = null;
          animating = false;
          collapsed = false;
          skipOneScrollTrigger = true;
          _screenVisible = false;
          fadeAnim.setValue(1);
          previousHeaderHeight = null;
        };
      }, []);
    }

    const flavorText =
      props.navMode === navModes.CASE
        ? I18n.t('messaging.patientFlavorText')
        : props.navMode === navModes.EMPLOYER
          ? I18n.t('messaging.providerFlavorText')
          : I18n.t('messaging.oncallFlavorText');

    function renderMembers() {
      return (
        <View style={[styles.membersContainer, !!props.isAirforce ? null : { paddingTop: 10 }]}>
          {!!(props.navMode === navModes.ONCALL) ? (
            <OnCallMembers />
          ) : (
            <ConversationMembers isAirforce={!!props.isAirforce} />
          )}
        </View>
      );
    }

    function renderPatientSelection() {
      if (props.navMode !== navModes.CASE) return null;
      return (
        <View style={styles.selectPatientContainer}>
          <Text style={styles.patientText}>Patient: </Text>
          <SelectPatient nativeContainerStyle={{ marginTop: 10, marginBottom: 5 }} />
        </View>
      );
    }

    return (
      <View style={styles.outerContainer}>
        <View style={styles.secondOuterContainer}>
          <Animated.View style={[styles.container, !__WEB__ ? { height: shrinkAnim } : null]}>
            <Animated.View style={[styles.membersAndSelectionContainer, { opacity: fadeAnim }]}>
              {!!__WEB__ && renderMembers()}
              {!__WEB__ && renderPatientSelection()}
              <View style={[styles.selectionContainer]}>
                <View style={[styles.selectContainer]}>
                  <Select
                    styles={SELECT_STYLE_ROUND}
                    closeOnSelect={true}
                    options={props.convoOptions}
                    placeholder={
                      props.navMode === navModes.ONCALL ? 'Medical Service' : 'You and...'
                    }
                    value={props.selectedOption}
                    onValue={handleSelectConvo}
                    webMode={true}
                    nativeContainerStyle={styles.nativeDropDownContainer}
                    nativeButtonStyle={styles.nativeButton}
                    nativeArrowIconStyle={styles.nativeArrowIcon}
                  />
                </View>
                {props.navMode !== navModes.ONCALL && (
                  <PlusButton
                    buttonStyle={styles.plusButton}
                    iconStyle={styles.plusIcon}
                    onPress={handlePlusPress}
                  />
                )}
              </View>
              {!__WEB__ && renderMembers()}
            </Animated.View>
            {!__WEB__ && (
              <>
                <Animated.View style={styles.collapsedView}>
                  <Text style={styles.collapsedLabel}>{props.selectedOption?.label}</Text>
                </Animated.View>
                <Pressable style={styles.arrowBtn} onPress={handleArrowPress}>
                  <Animated.Image
                    style={[
                      styles.icon,
                      {
                        transform: [
                          {
                            rotate: fadeAnim.interpolate({
                              inputRange: [0, 1],
                              outputRange: ['0deg', '180deg'],
                            }),
                          },
                        ],
                      },
                    ]}
                    source={require('../../../resources/arrow-down.png')}
                  />
                </Pressable>
              </>
            )}
          </Animated.View>
          {!props.messageCount && (
            <View style={styles.flavorTextContainer}>
              <FlavorMessage currentMessage={{ text: flavorText }} />
            </View>
          )}
        </View>
      </View>
    );
  })
);

const styles = StyleSheet.create({
  outerContainer: __WEB__
    ? {
        zIndex: 100,
      }
    : {
        zIndex: 100,
        height: 40,
        overflow: 'visible',
      },
  secondOuterContainer: __WEB__
    ? {}
    : {
        position: 'absolute',
        top: 0,
        right: 0,
        left: 0,
      },
  container: __WEB__
    ? {
        zIndex: 100,
      }
    : {
        zIndex: 100,
        borderBottomWidth: 1,
        borderBottomColor: COLOR.accent,
        overflow: 'hidden',
      },
  membersAndSelectionContainer: __WEB__
    ? {
        ...THEME.basicRow,
        backgroundColor: COLOR.altContrast,
        zIndex: 100,
        height: 130,
      }
    : {
        backgroundColor: COLOR.altContrast,
        zIndex: 100,
      },
  membersContainer: __WEB__
    ? {
        flex: 1,
        ...THEME.basicRow,
        height: 130,
      }
    : {
        height: 89,
        alignItems: 'center',
        justifyContent: 'center',
        // paddingBottom: 5,
      },
  selectionContainer: {
    ...THEME.basicRow,
    justifyContent: 'center',
    alignItems: 'center',
    width: __WEB__ ? 450 : 'auto',
    paddingRight: __WEB__ ? 30 : 10,
    borderLeftWidth: 1,
    borderColor: COLOR.accent,
    paddingLeft: __WEB__ ? 30 : 10,
    marginTop: __WEB__ ? 0 : 10,
    marginBottom: __WEB__ ? 0 : 10,
  },
  plusButton: {
    width: 40,
    height: 40,
    marginLeft: 20,
    borderColor: COLOR.accent,
    borderWidth: 1,
  },
  plusIcon: {
    height: 17,
    width: 17,
  },
  selectContainer: __WEB__
    ? {
        flex: 1,
        marginTop: 18,
      }
    : {
        flex: 1,
      },
  nativeDropDownContainer: {
    backgroundColor: COLOR.contrast,
    borderRadius: 20,
    borderColor: COLOR.textLight,
    borderWidth: 1,
    paddingHorizontal: 25,
    height: 40,
    marginBottom: 0,
    marginTop: 0,
  },
  nativeButton: {
    borderBottomWidth: 0,
    height: '100%',
    marginTop: 0,
    marginBottom: 0,
  },
  selectPatientContainer: {
    flexDirection: 'row',
    paddingHorizontal: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  patientText: {
    ...FONT.bold,
    color: COLOR.text,
    fontSize: 12,
    marginRight: 10,
  },
  icon: {
    width: 14,
    height: 14,
    tintColor: 'white',
  },
  arrowBtn: {
    backgroundColor: 'black',
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    bottom: 0,
    right: 0,
    zIndex: 1001,
  },
  collapsedView: {
    paddingHorizontal: 25,
    height: 40,
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    justifyContent: 'center',
  },
  collapsedLabel: {
    ...FONT.medium,
    fontSize: 14,
    color: COLOR.text,
  },
});
